<template>
    <PrimeButton
        :as="to ? 'router-link' : 'button'"
        :to="to"
        :class="[`tl-btn--${variant}`, { 'tl-btn--truncate-label': truncateLabel }]"
        :severity="computedSeverity"
        :size="primeSize"
        :text="isText"
        :outlined="isOutlined"
        :link="isLink"
        :loading="loading"
        :disabled="disabled"
        :type="type"
        v-bind="$attrs"
        @click="emit('click', $event)"
        @focus="emit('focus', $event)"
        @blur="emit('blur', $event)"
    >
        <SpinnerIcon
            v-if="loading"
            spin
            class="p-button-loading-icon p-button-icon p-button-icon-left"
        />
        <i
            v-else-if="iconLeading"
            class="p-button-icon p-button-icon-left"
            :class="iconLeading"
        />
        <span
            v-if="label"
            ref="labelEl"
            v-tooltip.bottom="truncateLabel && isLabelTruncated ? label : undefined"
            class="p-button-label"
            :class="{ truncate: truncateLabel }"
        >{{ label }}</span>
        <i
            v-if="iconTrailing"
            class="p-button-icon p-button-icon-right"
            :class="iconTrailing"
        />
        <LabelTag
            v-if="badge"
            :label="badge"
        />
    </PrimeButton>
</template>

<script setup lang="ts">
import { computed, useTemplateRef } from 'vue'
import PrimeButton from 'primevue/button'
import SpinnerIcon from '@primevue/icons/spinner'
import LabelTag from '@/components/atoms/LabelTag/LabelTag.vue'
import { useIsTruncated } from '@/composables/ui/useIsTruncated'
import type { ButtonProps } from './types'
import type { RouterLinkProps } from 'vue-router'

const emit = defineEmits<{
    click: [event: MouseEvent]
    focus: [event: FocusEvent]
    blur: [event: FocusEvent]
}>()

const props = withDefaults(defineProps<ButtonProps & Partial<RouterLinkProps>>(), {
    variant: 'primary',
    size: 'md',
    destructive: false,
    loading: false,
    disabled: false,
    type: 'button',
})

// Destructive overrides variant's severity but keeps its visual treatment (text/outlined/link).
const computedSeverity = computed(() => {
    if (props.destructive) {
        return 'danger'
    }
    switch (props.variant) {
        case 'secondary':
        case 'outline':
        case 'link-secondary':
            return 'secondary'
        case 'ghost':
            return 'contrast'
        default:
            return undefined
    }
})

// destructive secondary uses text mode so it maps to p-button-text p-button-danger.
// ghost (.tl-btn--ghost) then overrides back to transparent via CSS.
const isText = computed(() =>
    props.variant === 'ghost'
    || props.variant === 'link-secondary'
    || (props.destructive && props.variant === 'secondary'),
)
const isOutlined = computed(() => props.variant === 'outline')
const isLink = computed(() => props.variant === 'link')
const primeSize = computed(() => props.size === 'sm' ? 'small' : undefined)

const labelEl = useTemplateRef<HTMLElement>('labelEl')
const { isTruncated: isLabelTruncated } = useIsTruncated(labelEl, { enabled: props.truncateLabel })
</script>
